import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';

const Overlay = styled.div`${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}`;
const Modal = styled.div`${tw`bg-white rounded-lg p-8 w-full max-w-xl max-h-screen overflow-y-auto`}`;
const Title = tw.h2`text-xl font-bold mb-4 text-gray-800`;
const Label = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const Input = tw.input`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Select = tw.select`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Row = tw.div`flex gap-3 flex-wrap`;
const Col = styled.div`
  flex: 1;
  min-width: 200px;
`;
const ButtonRow = tw.div`flex justify-end gap-3 mt-4`;
const SaveBtn = tw.button`bg-green-500 text-white px-5 py-2 rounded font-semibold hover:bg-green-600 transition`;
const CancelBtn = tw.button`bg-gray-200 text-gray-700 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition`;

const Card = tw.div`border border-gray-200 rounded p-4 mb-4 bg-gray-500`;
const List = tw.div`divide-y divide-gray-100 max-h-screen overflow-y-auto mb-4 border-b border-t border-gray-100`;
const ListRow = tw.div`flex items-center justify-between py-2 text-sm`;

export default function AdminCoBuyForm({ item, onSave, onCancel }) {
    const [status, setStatus] = useState('active');
    const [driver, setDriver] = useState('');
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [dropoffAddress, setDropoffAddress] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (item) {
            setStatus(item.status || 'active');
            setDriver(item.driver || '');
            setExpectedDeliveryDate(item.expectedDeliveryDate || '');
            setDropoffAddress(item.dropoffAddress || '');
        }
    }, [item]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const data = {
                status,
                driver,
                expectedDeliveryDate,
                dropoffAddress
            };
            await onSave(data);
        } catch (err) {
            console.error('AdminCoBuyForm save error:', err);
            alert('Save failed: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    if (!item) return null;

    const pct = Math.min(100, Math.round(((item.currentAmount || 0) / item.targetAmount) * 100));

    return (
        <Overlay onClick={onCancel}>
            <Modal onClick={e => e.stopPropagation()}>
                <Title>Manage Co-Buying Group</Title>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                    Group Name: <strong style={{ color: '#374151' }}>{item.name}</strong>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.8rem', backgroundColor: '#f8fafc', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem' }}>
                    <div>Proximity Area: <strong>{item.neighborhood}</strong></div>
                    <div>Category: <strong>{item.bulkItem}</strong></div>
                    <div>Target Cost: <strong>₦{item.targetAmount?.toLocaleString()}</strong></div>
                    <div>Current Funding: <strong style={{ color: '#10b981' }}>₦{(item.currentAmount || 0).toLocaleString()} ({pct}%)</strong></div>
                </div>

                {/* Contributors */}
                <Card>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem', color: '#374151' }}>Pool Contributors ({item.members?.length || 0})</div>
                    <List>
                        {Array.isArray(item.members) && item.members.length > 0 ? (
                            item.members.map((m, idx) => (
                                <ListRow key={idx}>
                                    <div>
                                        <strong>{m.userName}</strong>
                                        <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 8 }}>({m.userEmail})</span>
                                        {m.items && m.items.length > 0 && (
                                            <div style={{ fontSize: '0.7rem', color: '#10b981' }}>Portion: {m.items[0].title}</div>
                                        )}
                                    </div>
                                    <strong style={{ color: '#10b981' }}>₦{m.amount.toLocaleString()}</strong>
                                </ListRow>
                            ))
                        ) : (
                            <div style={{ padding: '1rem', textAlign: 'center', fontStyle: 'italic', color: '#9ca3af', fontSize: '0.75rem' }}>No contributors yet</div>
                        )}
                    </List>
                </Card>

                {/* Form controls */}
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col>
                            <Label>Co-Op Group Status</Label>
                            <Select value={status} onChange={e => setStatus(e.target.value)}>
                                <option value="active">Active (Collecting Contributions)</option>
                                <option value="completed">Completed (Goal Reached, Awaiting Shipment)</option>
                                <option value="ordered">Ordered (Bulk Purchase Placed)</option>
                                <option value="delivered">Delivered to Central Drop-site</option>
                                <option value="failed">Failed / Cancelled</option>
                            </Select>
                        </Col>
                        <Col>
                            <Label>Central Drop-off Address</Label>
                            <Input value={dropoffAddress} onChange={e => setDropoffAddress(e.target.value)} placeholder="Central site address" />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Label>Assigned Delivery Driver</Label>
                            <Input value={driver} onChange={e => setDriver(e.target.value)} placeholder="e.g. Samuel Ade" />
                        </Col>
                        <Col>
                            <Label>Expected Drop-off Date</Label>
                            <Input type="date" value={expectedDeliveryDate} onChange={e => setExpectedDeliveryDate(e.target.value)} />
                        </Col>
                    </Row>

                    <ButtonRow>
                        <CancelBtn type="button" onClick={onCancel} disabled={saving}>Cancel</CancelBtn>
                        <SaveBtn type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </SaveBtn>
                    </ButtonRow>
                </form>
            </Modal>
        </Overlay>
    );
}
